import { useEffect, useState } from 'react';
import { SlideOver } from '@/components/SlideOver';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Alert } from '@/components/Alert';
import { FormSection } from '@/components/FormSection';
import { Button } from '@/components/Button';
import type { UpdateJobTaskInput, JobTaskStatus } from '@maker-toolchest/shared';

interface UpdateTaskStatusFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: UpdateJobTaskInput) => Promise<void>;
  currentStatus: JobTaskStatus;
  currentProgress: number;
  loading?: boolean;
  error?: string | null;
}

const statusOptions = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'QUEUED', label: 'Queued' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'FAILED', label: 'Failed' },
];

export function UpdateTaskStatusForm({
  open,
  onClose,
  onSubmit,
  currentStatus,
  currentProgress,
  loading = false,
  error,
}: UpdateTaskStatusFormProps) {
  const [status, setStatus] = useState(currentStatus);
  const [progress, setProgress] = useState(String(currentProgress));
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    if (open) {
      setStatus(currentStatus);
      setProgress(String(currentProgress));
      setStatusMessage('');
    }
  }, [open, currentStatus, currentProgress]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const data: UpdateJobTaskInput = {
      status: status as JobTaskStatus,
      progress: parseInt(progress, 10),
    };

    if (statusMessage.trim()) {
      data.statusMessage = statusMessage.trim();
    }

    await onSubmit(data);
  };

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title="Update Task Status"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" form="update-task-form" disabled={loading} loading={loading}>
            Update Task
          </Button>
        </>
      }
    >
      <form id="update-task-form" onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <Alert variant="error" message={error} />
        )}

        <FormSection title="Task Status" description="Update the status and progress of this task">
            <div className="space-y-4">
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-foreground mb-1">
                  Status *
                </label>
                <Select
                  id="status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as JobTaskStatus)}
                  options={statusOptions}
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="progress" className="block text-sm font-medium text-foreground mb-1">
                  Progress (%) *
                </label>
                <Input
                  id="progress"
                  type="number"
                  min="0"
                  max="100"
                  value={progress}
                  onChange={(e) => setProgress(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="statusMessage" className="block text-sm font-medium text-foreground mb-1">
                  Status Message
                </label>
                <Input
                  id="statusMessage"
                  type="text"
                  value={statusMessage}
                  onChange={(e) => setStatusMessage(e.target.value)}
                  placeholder="Optional message"
                  disabled={loading}
                />
              </div>
            </div>
          </FormSection>
        </form>
    </SlideOver>
  );
}
